import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type {
  EdpPackageSummary,
  EdpReviewer,
  EdpTemplatesResponse,
} from '../../api/employeeDocsTypes.js';
import { useSession } from '../../auth/SessionProvider.js';
import { Modal } from '../../components/Modal.js';
import { ReviewerPicker } from './ReviewerPicker.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextInput,
} from '../../components/ui.js';
import { PackageStatusBadge } from '../../employeeDocs/shared.js';
import { formatDate, formatRelative } from '../../lib/format.js';
import {
  EDP_NEXT_ACTION,
  EDP_PACKAGE_STATUSES,
  EDP_PACKAGE_STATUS_LABELS,
} from '../../../shared/employeeDocs.js';
import { PERMISSIONS } from '../../../shared/permissions.js';

interface ListResponse {
  configured: boolean;
  packages: EdpPackageSummary[];
}

export function EmployeeDocsListPage() {
  const { can } = useSession();
  const manages = can(PERMISSIONS.EMPLOYEE_DOCS_MANAGE);
  const [status, setStatus] = useState('');
  const [mine, setMine] = useState(!manages);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ['edp', 'packages', status, mine],
    queryFn: () =>
      apiRequest<ListResponse>('/api/employee-documents/packages', {
        query: { status: status || undefined, mine: mine ? 'true' : undefined },
      }),
  });

  return (
    <div>
      <PageHeader
        title="Employee documents"
        subtitle="Background check packages: the employee completes forms and uploads credentials, a VGS reviewer checks them, and the appointed Contracting Officer downloads the approved package."
        actions={
          manages ? (
            <>
              <Link
                to="/employee-documents/templates"
                className="rounded-md px-3.5 py-2 text-sm font-semibold text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              >
                Required documents
              </Link>
              <Button onClick={() => setCreating(true)}>New package</Button>
            </>
          ) : undefined
        }
      />

      {list.data && !list.data.configured && (
        <Alert variant="warning" className="mb-4" title="Document encryption is not configured">
          Packages cannot be created or opened until the EMPLOYEE_DOCS_ENCRYPTION_KEY is set for
          this environment.
        </Alert>
      )}

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Field label="Status" htmlFor="edp-status" className="w-56">
            <Select
              id="edp-status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">All</option>
              {EDP_PACKAGE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {EDP_PACKAGE_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          {manages && (
            <label className="flex items-center gap-2 pb-2 text-sm text-slate-800">
              <input
                type="checkbox"
                checked={mine}
                onChange={(event) => setMine(event.target.checked)}
              />
              Only packages I am reviewing
            </label>
          )}
        </div>

        {list.isLoading ? (
          <Spinner label="Loading packages" />
        ) : list.isError ? (
          <Alert variant="error">The packages could not be loaded.</Alert>
        ) : !list.data || list.data.packages.length === 0 ? (
          <EmptyState
            title="No packages"
            description={
              manages
                ? 'Create a package to invite an employee.'
                : 'Nothing is assigned to you for review.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Employee</th>
                  <th className="px-3 py-2">Package</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Waiting on</th>
                  <th className="px-3 py-2">Progress</th>
                  <th className="px-3 py-2">Reviewers</th>
                  <th className="px-3 py-2">USPS recipient</th>
                  <th className="px-3 py-2">Due</th>
                  <th className="px-3 py-2">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.data.packages.map((pkg) => (
                  <tr key={pkg.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <Link
                        to={`/employee-documents/${pkg.id}`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {pkg.employee.fullName}
                      </Link>
                      <p className="text-xs text-slate-500">{pkg.employee.email}</p>
                    </td>
                    <td className="px-3 py-2">{pkg.title}</td>
                    <td className="px-3 py-2">
                      <PackageStatusBadge status={pkg.status} reviewStarted={pkg.reviewStarted} />
                    </td>
                    <td className="px-3 py-2">{EDP_NEXT_ACTION[pkg.status]}</td>
                    <td className="px-3 py-2">
                      {pkg.completeCount}/{pkg.itemCount}
                      {pkg.rejectedCount > 0 && (
                        <span className="ml-1 text-red-700">({pkg.rejectedCount} returned)</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {pkg.reviewers.map((reviewer) => reviewer.fullName).join(', ')}
                    </td>
                    <td className="px-3 py-2">{pkg.recipient.name}</td>
                    <td className="px-3 py-2">{pkg.dueAt ? formatDate(pkg.dueAt) : '—'}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {formatRelative(
                        pkg.releasedAt ?? pkg.submittedAt ?? pkg.invitedAt ?? pkg.createdAt,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && <CreatePackageModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreatePackageModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const templates = useQuery({
    queryKey: ['edp', 'templates'],
    queryFn: () => apiRequest<EdpTemplatesResponse>('/api/employee-documents/templates'),
  });
  const reviewers = useQuery({
    queryKey: ['edp', 'reviewers'],
    queryFn: () => apiRequest<EdpReviewer[]>('/api/employee-documents/reviewers'),
  });

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    title: 'Background check package',
    packageTemplateId: '',
    recipientName: '',
    recipientEmail: '',
    dueAt: '',
  });
  const [extraIds, setExtraIds] = useState<string[]>([]);
  const [reviewerIds, setReviewerIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const packageTemplate = templates.data?.packages.find(
    (entry) => entry.id === form.packageTemplateId,
  );
  const included = new Set(packageTemplate?.requirementIds ?? []);

  const packages = templates.data?.packages;
  useEffect(() => {
    if (packages?.length === 1 && packages[0]) chooseTemplate(packages[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages]);

  function chooseTemplate(id: string) {
    const template = packages?.find((entry) => entry.id === id);
    if (template && template.defaultReviewerUserIds.length > 0) {
      setReviewerIds(template.defaultReviewerUserIds);
    }
    setForm((current) => ({
      ...current,
      packageTemplateId: id,
      recipientName: template?.defaultRecipientName ?? current.recipientName,
      recipientEmail: template?.defaultRecipientEmail ?? current.recipientEmail,
    }));
  }

  const create = useMutation({
    mutationFn: () =>
      apiRequest<EdpPackageSummary>('/api/employee-documents/packages', {
        method: 'POST',
        body: {
          employee: { fullName: form.fullName, email: form.email, phone: form.phone || undefined },
          title: form.title,
          packageTemplateId: form.packageTemplateId || null,
          requirementTemplateIds: extraIds.filter((id) => !included.has(id)),
          reviewerUserIds: reviewerIds,
          recipientName: form.recipientName,
          recipientEmail: form.recipientEmail,
          dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
        },
      }),
    onSuccess: (pkg) => {
      void queryClient.invalidateQueries({ queryKey: ['edp', 'packages'] });
      onClose();
      navigate(`/employee-documents/${pkg.id}`);
    },
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The package could not be created.',
      ),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!form.packageTemplateId && extraIds.length === 0) {
      setError('Choose a package template or at least one required document.');
      return;
    }
    if (reviewerIds.length === 0) {
      setError('Choose at least one QA reviewer.');
      return;
    }
    create.mutate();
  };

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const requirements = (templates.data?.requirements ?? []).filter((entry) => entry.isActive);

  return (
    <Modal
      open
      onClose={onClose}
      title="New employee document package"
      description="The employee receives an email with a secure link. The reviewer is emailed when the employee submits; the Contracting Officer is emailed only after the reviewer approves."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="edp-create" loading={create.isPending}>
            Create and invite
          </Button>
        </>
      }
    >
      <form id="edp-create" onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert variant="error">{error}</Alert>}

        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-slate-900">Employee</legend>
          <Field label="Full name" htmlFor="c-name" required>
            <TextInput
              id="c-name"
              required
              value={form.fullName}
              onChange={(e) => set('fullName')(e.target.value)}
            />
          </Field>
          <Field label="Email" htmlFor="c-email" required>
            <TextInput
              id="c-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => set('email')(e.target.value)}
            />
          </Field>
          <Field label="Phone" htmlFor="c-phone">
            <TextInput
              id="c-phone"
              value={form.phone}
              onChange={(e) => set('phone')(e.target.value)}
            />
          </Field>
          <Field label="Due date" htmlFor="c-due">
            <TextInput
              id="c-due"
              type="date"
              value={form.dueAt}
              onChange={(e) => set('dueAt')(e.target.value)}
            />
          </Field>
        </fieldset>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-sm font-semibold text-slate-900">Documents</legend>
          <Field label="Package title" htmlFor="c-title" required>
            <TextInput
              id="c-title"
              required
              value={form.title}
              onChange={(e) => set('title')(e.target.value)}
            />
          </Field>
          <Field label="Package template" htmlFor="c-template">
            <Select
              id="c-template"
              value={form.packageTemplateId}
              onChange={(e) => chooseTemplate(e.target.value)}
            >
              <option value="">None — pick documents individually</option>
              {(templates.data?.packages ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </Select>
          </Field>
          {templates.isLoading ? (
            <Spinner label="Loading documents" />
          ) : (
            <div className="grid gap-1 sm:grid-cols-2">
              {requirements.map((entry) => {
                const fixed = included.has(entry.id);
                return (
                  <label key={entry.id} className="flex items-start gap-2 text-sm text-slate-800">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={fixed || extraIds.includes(entry.id)}
                      disabled={fixed}
                      onChange={(e) =>
                        setExtraIds((current) =>
                          e.target.checked
                            ? [...current, entry.id]
                            : current.filter((id) => id !== entry.id),
                        )
                      }
                    />
                    <span>
                      {entry.name}
                      <span className="text-xs text-slate-500">
                        {' '}
                        · {entry.kind === 'form' ? 'form to fill in' : 'upload'}
                        {fixed && ' · in template'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>

        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-slate-900">Review and delivery</legend>
          <Field
            label="VGS QA reviewers"
            htmlFor="c-reviewer"
            required
            className="sm:col-span-2"
            hint="Tick everyone who may review this package; any one of them can accept, reject and release it."
          >
            <ReviewerPicker
              idPrefix="c-reviewer"
              reviewers={reviewers.data ?? []}
              selected={reviewerIds}
              onChange={setReviewerIds}
            />
          </Field>
          <Field label="Contracting Officer name" htmlFor="c-rname" required>
            <TextInput
              id="c-rname"
              required
              value={form.recipientName}
              onChange={(e) => set('recipientName')(e.target.value)}
            />
          </Field>
          <Field label="Contracting Officer email" htmlFor="c-remail" required>
            <TextInput
              id="c-remail"
              type="email"
              required
              value={form.recipientEmail}
              onChange={(e) => set('recipientEmail')(e.target.value)}
            />
          </Field>
        </fieldset>
      </form>
    </Modal>
  );
}
